import { useState } from 'react'
import { isAxiosError } from 'axios'

import { captureAxiosError, logSentry } from '@gto/sentry'

/**
 * Optional extractor that pulls a partner-actionable detail string out of a
 * failure. Returning `null` leaves the inline error rendering the generic
 * fallback (`MutationError`'s `messageId`). Observability is unchanged — the
 * raw error still routes through `captureAxiosError` / `logSentry`.
 */
type ExtractDetail = (error: unknown) => string | null

type UseMutationErrorHandlerOptions = {
  extractDetail?: ExtractDetail
}

/**
 * Shared mutation error sink for operator/partner write surfaces. Forwards every
 * failure to Sentry (captureAxiosError for axios errors — keeps request metadata —
 * logSentry otherwise) and exposes a boolean so the caller can render the inline
 * {@link import('@/components/mutation-error').MutationError} Alert. The app has
 * no SnackbarProvider, so the user-visible surface is an inline Alert rather than
 * a toast.
 *
 * Optional `extractDetail` lets a call site surface a sanitised backend message
 * (e.g. a Django Ninja `CustomValidationError.detail` on a 422) inside the
 * inline Alert instead of the generic fallback — useful when the BE response
 * carries an actionable reason the partner can act on. Sentry observability is
 * preserved either way: the raw error still flows through the same capture path
 * above.
 *
 * The single owner of the "a write mutation failed, tell the user and Sentry"
 * concern across the affiliate portal — admin settlement/payout/partner actions
 * and the partner-facing T&C acceptance all route through it.
 */
export const useMutationErrorHandler = (
  logPrefix: string,
  options?: UseMutationErrorHandlerOptions,
) => {
  const [hasError, setHasError] = useState(false)
  const [detail, setDetail] = useState<string | null>(null)
  const extractDetail = options?.extractDetail

  const onError = (e: unknown) => {
    if (isAxiosError(e)) {
      captureAxiosError(e, 'react')
    } else {
      logSentry(logPrefix, e)
    }
    setDetail(extractDetail ? extractDetail(e) : null)
    setHasError(true)
  }

  // Surface the inline error WITHOUT a second Sentry report. For a failure whose
  // observability is already owned upstream (e.g. a refetch that reported itself),
  // routing it through `onError` would double-log the one event under this
  // handler's unrelated prefix. `showError` is the UI-only half of `onError` —
  // including resetting `detail`, so a prior onError's BE detail string can
  // never leak into a showError-triggered render.
  const showError = () => {
    setDetail(null)
    setHasError(true)
  }

  const clearError = () => {
    setHasError(false)
    setDetail(null)
  }

  return { hasError, detail, onError, showError, clearError }
}

/**
 * The state shape returned by {@link useMutationErrorHandler}. Exported so
 * call sites can pass the handler to child dialogs as a single prop instead
 * of drilling `hasError` and `clearError` separately.
 */
export type MutationErrorState = ReturnType<typeof useMutationErrorHandler>
